import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { ArrowRight, Scales } from "@phosphor-icons/react";
import SEO from "@/components/SEO";
import PublicNav from "@/components/PublicNav";
import { DTMComparisonBarChart } from "@/components/charts/DTMComparisonBarChart";
import { ToolCTA, InlineNewsletter, ShareBar, RelatedResearch } from "@/components/InlineCTA";

const CHART_A_DATA = [
  { label: ">75% gap", seriesA: 9.52, seriesB: 7.29 },
  { label: ">85% gap", seriesA: 9.67, seriesB: 7.02 },
  { label: ">96% gap", seriesA: 10.01, seriesB: 6.68 },
];

const FAQ_SCHEMA = {
  "@context": "https://schema.org",
  "@type": "FAQPage",
  mainEntity: [
    {
      "@type": "Question",
      name: "What is a lopsided trade in fantasy sports?",
      acceptedAnswer: { "@type": "Answer", text: "A trade where one side receives meaningfully more value than the other on paper. In dynasty, most trades are somewhat uneven, so \"lopsided\" usually means a large gap — in our analysis, 75% or more, which is roughly the top 10% most one-sided deals." },
    },
    {
      "@type": "Question",
      name: "Why would someone make a trade that seems uneven?",
      acceptedAnswer: { "@type": "Answer", text: "Usually because they're a contender buying now. They knowingly give up future value, depth, or picks to land a player who pushes them toward a title. They're not confused about value; they've decided a championship is worth paying a premium for." },
    },
    {
      "@type": "Question",
      name: "How do you assess whether a lopsided trade is beneficial?",
      acceptedAnswer: { "@type": "Answer", text: "Look past the value gap to your situation. A smart overpay means you're a genuine contender, the player fills a real starting-lineup need, and you're giving up surplus rather than core pieces. If you're overpaying for a name, gutting your roster, or rebuilding at win-now prices, it's probably a bad deal." },
    },
    {
      "@type": "Question",
      name: "Can lopsided trades lead to winning a fantasy league?",
      acceptedAnswer: { "@type": "Answer", text: "Yes, and they do more often than you'd expect. In our data of 27,000+ lopsided dynasty trades, the side that \"lost\" on value won the championship more often than the side that \"won\" it — up to 10.0% versus 6.7% in the most extreme deals." },
    },
  ],
};

function StatCard({ label, value }) {
  return (
    <div className="border border-[#262626] bg-[#171717] p-3 text-center">
      <p className="font-mono text-xl font-bold text-white">{value}</p>
      <p className="text-[9px] font-bold uppercase text-[#555] mt-1">{label}</p>
    </div>
  );
}

export default function LopsidedTradesResearch() {
  return (
    <div className="min-h-screen bg-[#0a0a0a]" data-testid="lopsided-trades-research">
      <SEO
        title={"Who Actually Wins Lopsided Trades? (27,000 Real Ones, Checked) \u2014 Dynasty Trade Maker"}
        description={"Winning a lopsided fantasy trade on paper doesn\u2019t mean winning your league. Here\u2019s what 27,000 real lopsided dynasty trades reveal."}
        path="/research/who-wins-lopsided-trades"
        image="https://dynastytrademaker.com/hero-images/who-wins-lopsided-trades.jpg"
      />
      <Helmet>
        <script type="application/ld+json">{JSON.stringify(FAQ_SCHEMA)}</script>
      </Helmet>
      <PublicNav />

      <article className="max-w-3xl mx-auto px-4 py-8">
        <Link to="/research" className="text-[10px] text-[#555] hover:text-[#a3a3a3] no-underline uppercase tracking-wider">Research</Link>

        <span className="text-[10px] font-bold uppercase tracking-[0.3em] text-[#ccff00] block mt-4 mb-2">27,485 LOPSIDED TRADES ANALYZED</span>
        <h1 className="font-heading text-3xl sm:text-4xl tracking-tight text-white uppercase mb-4" data-testid="article-title">

        {/* Hero Image */}
        <div className="my-6 border border-[#262626] rounded-sm overflow-hidden" data-testid="hero-image">
          <img src="/hero-images/who-wins-lopsided-trades.jpg" alt="" className="w-full h-auto" loading="eager" />
        </div>
        <ShareBar title="Who Actually Wins Lopsided Trades?" path="/research/who-wins-lopsided-trades" />
          WHO ACTUALLY WINS <span className="text-[#ccff00]">LOPSIDED TRADES?</span>
        </h1>
        <p className="text-base text-[#a3a3a3] mb-4">
          {"We pulled every heavily lopsided trade from thousands of completed Sleeper dynasty leagues \u2014 more than 27,000 of them \u2014 and checked who actually won the league afterward. The result flips the whole idea of \u201Cwinning a trade\u201D on its head."}
        </p>
        <p className="text-sm font-semibold text-white mb-8">
          {"The team that comes out "}
          <span className="text-[#ccff00]">behind</span>
          {" on value wins championships more often than the team that comes out ahead."}
        </p>


        <div className="space-y-10 text-sm text-[#a3a3a3] leading-relaxed mt-8">

          {/* ── What Counts ────────────────── */}
          <section>
            <h2 className="font-heading text-2xl text-white uppercase mb-3">WHAT COUNTS AS A LOPSIDED TRADE</h2>
            <p className="mb-3">
              {"In dynasty, almost no trade is perfectly even. A truly lopsided trade is one where the value gap is large \u2014 one side is getting meaningfully more on paper than the other."}
            </p>
            <p>
              {"We measured this by comparing the total value each side received and calculating the gap. For this analysis we focused on trades with a value gap of "}
              <strong className="text-white">75% or more</strong>
              {", roughly the top 10% most lopsided deals in all of dynasty. Then we split them further: the very lopsided (85%+) and the extreme outliers (96%+) \u2014 the deals that look like outright robbery on a calculator."}
            </p>
          </section>

          {/* ── The Finding ────────────────── */}
          <section>
            <h2 className="font-heading text-2xl text-white uppercase mb-3">{"THE STRATEGIC ADVANTAGE OF \u201CLOSING\u201D A TRADE"}</h2>

            <div className="grid grid-cols-3 gap-3 mb-5">
              <StatCard label="Lopsided Trades" value="27,485" />
              <StatCard label={"Value \u201CLoser\u201D Champ Rate"} value="10.0%" />
              <StatCard label={"Value \u201CWinner\u201D Champ Rate"} value="6.7%" />
            </div>

            <p className="mb-4">
              {"In trades with a 75%+ value gap, the value \u201Closer\u201D won the title "}
              <strong className="text-[#ccff00]">9.52%</strong>
              {" of the time versus "}
              <strong className="text-white">7.29%</strong>
              {" for the value \u201Cwinner.\u201D Push to the extreme outliers \u2014 the 96%+ gap trades \u2014 and the spread widens: the \u201Closer\u201D won "}
              <strong className="text-[#ccff00]">10.01%</strong>
              {" of the time, the \u201Cwinner\u201D just "}
              <strong className="text-white">6.68%</strong>
              {". The team getting supposedly fleeced won about "}
              <strong className="text-[#ccff00]">50% more often</strong>
              {"."}
            </p>

            <DTMComparisonBarChart
              data={CHART_A_DATA}
              title={"Who Wins the Championship After a Lopsided Trade?"}
              subtitle={"Championship rate by which side got more value in the deal"}
              source={"DTM Master Data Export \u2014 A5.csv | 27,485 lopsided trades (\u226575% gap)"}
              seriesAKey="seriesA"
              seriesBKey="seriesB"
              seriesAName={"Value Loser (\u201Coverpaid\u201D)"}
              seriesBName={"Value Winner (got more)"}
              seriesAColor="#ccff00"
              seriesBColor="#ff4d4d"
              height={220}
              formatter={(v) => `${v}%`}
              domain={[5, 11]}
            />

            <p className="mt-5">
              {"The worse a trade looks for you on paper, on average, the "}
              <em>better</em>
              {" your championship odds. That doesn\u2019t mean you should donate your best players. It means the value gap alone is telling you almost the opposite of what you think."}
            </p>
          </section>

          {/* ── Why This Happens ────────────────── */}
          <section>
            <h2 className="font-heading text-2xl text-white uppercase mb-3">WHY THIS HAPPENS</h2>
            <p className="mb-3">{"The explanation isn\u2019t magic \u2014 it\u2019s motivation. Think about who\u2019s on each side of a wildly lopsided trade."}</p>
            <div className="border border-[#262626] bg-[#171717] p-5 space-y-4 mb-4">
              <div>
                <p className="text-sm font-bold text-[#ccff00] mb-1">{"\u201COverpaying\u201D Side (Value Loser)"}</p>
                <p className="text-sm text-[#a3a3a3]">{"Almost always a contender. They\u2019ve decided this is their year and they\u2019re willing to give up future value, depth, or picks to land the one player who pushes them over the top. They\u2019re not confused about value \u2014 they\u2019re buying a championship."}</p>
              </div>
              <div>
                <p className="text-sm font-bold text-[#ff4d4d] mb-1">{"\u201CWinning\u201D Side (Value Winner)"}</p>
                <p className="text-sm text-[#a3a3a3]">{"Usually a rebuilder or a team out of it, cashing in a win-now asset for future value they won\u2019t use for a while. They\u2019re doing the smart long-term thing. But the smart long-term thing and winning this season are different goals."}</p>
              </div>
            </div>
            <p>{"So the value gap isn\u2019t measuring who got ripped off. It\u2019s measuring "}<strong className="text-white">{"who\u2019s going for it"}</strong>{". And going for it, aggressively, at the right moment, is how titles get won."}</p>
          </section>

          <ToolCTA
            text="See exactly where any trade falls"
            subtext="Run any deal through the calculator to see the real value gap before you decide."
            buttonText="TRADE FINDER"
            buttonLink="/features/trade-finder"
            icon={<Scales size={12} weight="bold" />}
          />

          {/* ── Smart vs Bad ────────────────── */}
          <section>
            <h2 className="font-heading text-2xl text-white uppercase mb-3">{"HOW TO TELL A BAD TRADE FROM A SMART \u201COVERPAY\u201D"}</h2>
            <div className="border border-[#262626] bg-[#171717] p-5 space-y-3 mb-4">
              <div className="flex items-start gap-3">
                <span className="text-[#ccff00] font-bold text-xs shrink-0 mt-0.5">{"\u2713"}</span>
                <span className="text-sm text-[#d4d4d4]"><strong className="text-white">{"You\u2019re a real contender"}</strong>{" \u2014 not a team convincing yourself you\u2019re close."}</span>
              </div>
              <div className="flex items-start gap-3">
                <span className="text-[#ccff00] font-bold text-xs shrink-0 mt-0.5">{"\u2713"}</span>
                <span className="text-sm text-[#d4d4d4]"><strong className="text-white">The player fills a real hole</strong>{" in your starting lineup, not your bench."}</span>
              </div>
              <div className="flex items-start gap-3">
                <span className="text-[#ccff00] font-bold text-xs shrink-0 mt-0.5">{"\u2713"}</span>
                <span className="text-sm text-[#d4d4d4]"><strong className="text-white">{"You\u2019re giving up surplus"}</strong>{" \u2014 future value or depth, not gutting the core that makes you good."}</span>
              </div>
              <div className="flex items-start gap-3">
                <span className="text-[#ccff00] font-bold text-xs shrink-0 mt-0.5">{"\u2713"}</span>
                <span className="text-sm text-[#d4d4d4]"><strong className="text-white">The timing is right</strong>{" \u2014 a championship is genuinely in reach, not a vacuum."}</span>
              </div>
            </div>
            <p>
              {"A bad lopsided trade is the reverse: overpaying for a name, not a need. Giving up pieces you actually need. Or rebuilding at win-now prices. If you can\u2019t clearly say which weakness this trade fixes and why now is the moment, the gap probably is just a bad deal."}
            </p>
          </section>

          {/* ── League Dynamics ────────────────── */}
          <section>
            <h2 className="font-heading text-2xl text-white uppercase mb-3">LEAGUE DYNAMICS CHANGE THE MATH</h2>
            <p className="mb-4">
              {"Format matters. The \u201Closing trades win\u201D effect is strongest in "}
              <strong className="text-white">Superflex</strong>
              {" leagues and much smaller in 1QB."}
            </p>
            <div className="grid grid-cols-2 gap-3 mb-4">
              <div className="border border-[#262626] bg-[#171717] p-4 text-center">
                <p className="text-[9px] font-bold uppercase text-[#ccff00] mb-2">SUPERFLEX (96%+ GAP)</p>
                <p className="font-mono text-lg text-white">10.06% <span className="text-[10px] text-[#555]">vs</span> 6.63%</p>
                <p className="text-[9px] text-[#555] mt-1">{"loser \u2192 winner champ rate"}</p>
              </div>
              <div className="border border-[#262626] bg-[#171717] p-4 text-center">
                <p className="text-[9px] font-bold uppercase text-[#ccff00] mb-2">1QB (96%+ GAP)</p>
                <p className="font-mono text-lg text-white">9.35% <span className="text-[10px] text-[#555]">vs</span> 7.23%</p>
                <p className="text-[9px] text-[#555] mt-1">{"loser \u2192 winner champ rate"}</p>
              </div>
            </div>
            <p>
              {"Superflex creates more genuine win-now desperation, especially around quarterbacks. The more competitive and deep your format, the more a big \u201Coverpay\u201D tends to signal a contender who knows exactly what they\u2019re doing."}
            </p>
          </section>

          <InlineNewsletter context="championship" source="lopsided-trades-research" />

          {/* ── Takeaway ────────────────── */}
          <section>
            <h2 className="font-heading text-2xl text-white uppercase mb-3">THE TAKEAWAY</h2>
            <p className="mb-3">
              {"Stop trying to win every trade on paper. The data is clear: in the most lopsided dynasty trades, the team that \u201Clost\u201D on value won the championship more often than the team that \u201Cwon\u201D it."}
            </p>
            <p>
              {"The next time you\u2019re staring at an offer that looks a little rich, don\u2019t reflexively say no because a calculator frowns at it. Ask whether you\u2019re a contender, whether it fixes a real need, and whether the timing is right. If the answer is yes, the \u201Coverpay\u201D might be the best trade you make all year."}
            </p>
            <p className="mt-3">
              {"Want to see exactly where a trade falls? Check the "}
              <Link to="/rankings" className="text-[#ccff00] no-underline hover:underline">dynasty trade value chart</Link>
              {" or run the deal through our "}
              <Link to="/trades" className="text-[#ccff00] no-underline hover:underline">trade database</Link>
              {" before you decide."}
            </p>
          </section>

          {/* ── FAQ ────────────────── */}
          <section className="mt-12 border-t border-[#262626] pt-8">
            <h2 className="font-heading text-2xl text-white uppercase mb-5">FREQUENTLY ASKED QUESTIONS</h2>
            <div className="space-y-5">
              {FAQ_SCHEMA.mainEntity.map((q, i) => (
                <div key={i} className="border border-[#262626] bg-[#171717] p-4 rounded-sm">
                  <h3 className="text-sm font-bold text-white mb-2">{q.name}</h3>
                  <p className="text-sm text-[#a3a3a3] leading-relaxed">{q.acceptedAnswer.text}</p>
                </div>
              ))}
            </div>
          </section>
        </div>

        {/* ── Share + Bottom CTA ────────────────── */}
        <div className="mt-10 mb-6">
          <ShareBar title="Who Actually Wins Lopsided Trades?" path="/research/who-wins-lopsided-trades" />
        </div>
        <div className="border border-[#ccff00]/30 bg-[#ccff00]/5 p-6 text-center">
          <h3 className="font-heading text-lg text-white uppercase mb-2">CHECK ANY TRADE BEFORE YOU MAKE IT</h3>
          <p className="text-xs text-[#888] mb-4">{"See the real value gap and whether you\u2019re buying a championship or making a mistake."}</p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
            <Link to="/trade-calculator" className="h-9 px-5 bg-[#ccff00] text-black rounded-sm text-[11px] inline-flex items-center gap-1.5 no-underline font-bold hover:bg-[#b8e600] transition-colors" data-testid="cta-trade-calculator">TRADE CALCULATOR</Link>
            <Link to="/research" className="text-[11px] text-[#ccff00] no-underline hover:underline flex items-center gap-1" data-testid="cta-more-research">More Research <ArrowRight size={11} /></Link>
          </div>
        </div>

        <RelatedResearch slug="who-wins-lopsided-trades" />
        <p className="mt-6 text-center text-[10px] text-[#444] italic">
          {"Based on 27,485 lopsided trades (\u226575% value gap) from thousands of championship Sleeper dynasty leagues, 2021\u20132026. Trade values use current FantasyCalc values, not as-of-trade-date values."}
        </p>

        <div className="mt-4 text-center text-[10px] text-[#444] space-x-3">
          <Link to="/research" className="hover:text-[#a3a3a3] no-underline">Research</Link>
          <Link to="/trade-calculator" className="hover:text-[#a3a3a3] no-underline">Calculator</Link>
          <Link to="/trades" className="hover:text-[#a3a3a3] no-underline">Trade Database</Link>
          <Link to="/research/do-teams-that-trade-more-win" className="hover:text-[#a3a3a3] no-underline">Trade Volume</Link>
        </div>
      </article>
    </div>
  );
}
